import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';

const TenantLayout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('John');
  const [userAvatar, setUserAvatar] = useState('');
  const [userRole, setUserRole] = useState('Tenant');
  
  const tenantMenuItems = [
    { path: '/tenant/properties', label: 'Properties List', icon: '/assets/images/sidebar_properties.png' },
    { path: '/tenant/screenings', label: 'My Screenings', icon: '/assets/images/sidebardashboardIcon.svg' },
    { path: '/tenant/leases', label: 'My Leases', icon: '/assets/images/file-04.png' },
    { path: '/tenant/notifications', label: 'Notifications', icon: '/assets/images/sidebarNotificationIcon.svg' },
    { path: '/tenant/settings', label: 'Profile', icon: '/assets/images/sidebarSettingsIcon.svg' },
  ];
  
  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      // First try to get from userData
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const userDetails = userData?.user_details || {};
        const roleValue = userDetails?.role ?? userData?.role ?? localStorage.getItem('role');
        const normalizedRole =
          roleValue === 1 || roleValue === '1' || roleValue === 'landlord'
            ? 'Landlord'
            : roleValue === 2 || roleValue === '2' || roleValue === 'tenant'
              ? 'Tenant'
              : '';
        const firstName = userDetails?.first_name || '';
        const lastName = userDetails?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
          return {
            userName: fullName,
            // Only use actual profile image; otherwise let Header show initials
            userAvatar: userDetails?.profile_image || '',
            userRole: normalizedRole || 'Tenant',
          };
        }
      }
      // Fallback to userName if userData doesn't have name
      const storedUserName = localStorage.getItem('userName');
      const storedRole = localStorage.getItem('role');
      const normalizedStoredRole =
        storedRole === '1' || storedRole === 1 || storedRole === 'landlord'
          ? 'Landlord'
          : storedRole === '2' || storedRole === 2 || storedRole === 'tenant'
            ? 'Tenant'
            : '';
      if (storedUserName) {
        return {
          userName: storedUserName,
          userAvatar: localStorage.getItem('userAvatar') || '',
          userRole: normalizedStoredRole || 'Tenant',
        };
      }
    } catch (error) {
      console.error('Error reading user info from localStorage:', error);
    }
    return { userName: 'John', userAvatar: '', userRole: 'Tenant' };
  };
  
  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Update user info when component mounts or storage changes
  useEffect(() => {
    const updateUserInfo = () => {
      const { userName: name, userAvatar: avatar, userRole: roleLabel } = getUserInfo();
      setUserName(name);
      setUserAvatar(avatar);
      setUserRole(roleLabel || 'Tenant');
    };
    
    // Initial load
    updateUserInfo();
    
    // Listen for custom event when userData is updated (from same window)
    const handleUserDataUpdate = () => {
      updateUserInfo();
    };
    
    // Also listen for storage events (when localStorage changes from other tabs/windows)
    const handleStorageChange = () => {
      updateUserInfo();
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get title and subtitle based on current route
  const getTitleAndSubtitle = () => {
    const path = location.pathname;
    if (path.includes('/properties')) return { title: 'Properties List', subtitle: 'Browse available rental listings' };
    if (path.includes('/screenings')) return { title: 'My Screenings', subtitle: 'View your screening applications' };
    if (path.includes('/leases')) return { title: 'My Leases', subtitle: 'Your rent subscriptions and payment schedule' };
    if (path.includes('/notifications')) return { title: 'Notifications', subtitle: 'Stay updated on your account activity' };
    if (path.includes('/settings')) return { title: 'Settings', subtitle: 'Manage your account and preferences' };
    return { title: 'Dashboard', subtitle: null };
  };

  // Get breadcrumb based on current route
  const getBreadcrumb = () => {
    const path = location.pathname;
    
    // Check for screening detail page
    if (path.match(/\/tenant\/screenings\/\d+$/)) {
      const screeningId = path.match(/\/screenings\/(\d+)/)?.[1];
      return [
        { label: 'My Screenings', path: '/tenant/screenings' },
        { label: 'Screening Detail', path: null }
      ];
    }
    
    // Check for lease detail page (the route param is the property id)
    if (path.match(/^\/tenant\/leases\/[^/]+$/)) {
      return [
        { label: 'My Leases', path: '/tenant/leases' },
        { label: 'Lease Detail', path: null }
      ];
    }
    
    // Check for settings nested routes
    if (path.match(/\/tenant\/settings\/document$/)) {
      return [
        { label: 'Settings', path: '/tenant/settings' },
        { label: 'Documents', path: null }
      ];
    }
    
    if (path.match(/\/tenant\/settings\/change-password$/)) {
      return [
        { label: 'Settings', path: '/tenant/settings' },
        { label: 'Change Password', path: null }
      ];
    }

    if (path.match(/\/tenant\/settings\/background-check$/)) {
      return [
        { label: 'Settings', path: '/tenant/settings' },
        { label: 'Background Check', path: null }
      ];
    }
    
    // Settings index (Profile) - optional, can show breadcrumb or just title
    // For consistency, we'll return null here and let the title show
    
    return null;
  };

  const { title, subtitle } = getTitleAndSubtitle();
  const breadcrumb = getBreadcrumb();

  return (
    <div className="tenant-layout">
      <Sidebar 
        menuItems={tenantMenuItems} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />
      <div className="tenant-layout-content">
        <Header 
          title={title} 
          subtitle={subtitle} 
          userName={userName} 
          userAvatar={userAvatar}
          userRole={userRole}
          breadcrumb={breadcrumb}
          onSidebarToggle={() => setSidebarOpen(true)}
        />
        <main className="tenant-layout-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default TenantLayout;
